import React, { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import DataService from '../services/DataService';

const HorseRaceWidget = () => {
  const [predictions, setPredictions] = useState([]);
  const [activeRaceTab, setActiveRaceTab] = useState(0);
  const [isTabSwitching, setIsTabSwitching] = useState(false);
  const params = useParams();
  
  useEffect(() => {
    const loadData = async () => {
      const raceName = params.raceName || 'sample-race';
      try {
        console.log('Loading race data for:', raceName);
        const data = await DataService.fetchRaceData(raceName);
        console.log('Loaded data:', data);
        setPredictions(data.records || []);
      } catch (error) {
        console.error('Error loading race data:', error);
        setPredictions([]);
      }
    };

    loadData();
  }, [params.raceName]);

  // Get all unique races from predictions
  const getAllRaces = () => {
    const races = [];
    if (predictions.length > 0) {
      const sample = predictions[0].fields;
      if (sample.Race) races.push({ 
        name: sample.Race, 
        url: sample.RaceURL, 
        purse: sample.Purse,
        picks: ['One', 'Two', 'Three', 'Alternate']
      });
      if (sample.RaceTwo) races.push({ 
        name: sample.RaceTwo, 
        url: sample.RaceTwoURL, 
        purse: sample.PurseTwo,
        picks: ['Four', 'Five', 'Six', 'AlternateTwo']
      });
      if (sample.RaceThree) races.push({ 
        name: sample.RaceThree, 
        url: sample.RaceThreeURL, 
        purse: sample.PurseThree,
        picks: ['Seven', 'Eight', 'Nine', 'AlternateThree']
      });
      if (sample.RaceFour) races.push({ 
        name: sample.RaceFour, 
        url: sample.RaceFourURL, 
        purse: sample.PurseFour,
        picks: ['Ten', 'Eleven', 'Twelve', 'AlternateFour']
      });
    }
    return races;
  };

  const races = getAllRaces();
  const currentRace = races[activeRaceTab];

  // Enhanced tab switching with debounce
  const handleTabChange = (newTabIndex) => {
    if (isTabSwitching || newTabIndex === activeRaceTab) return;
    
    setIsTabSwitching(true);
    setActiveRaceTab(newTabIndex);
    
    // Reset debounce after animation
    setTimeout(() => {
      setIsTabSwitching(false);
    }, 150);
  };

  const ExpertAvatar = ({ expert }) => {
    const avatarUrl = expert.Avatar && expert.Avatar[0] && expert.Avatar[0].url ? expert.Avatar[0].url : null;
    
    const handleImageError = (e) => {
      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(expert.Name)}&size=40&background=008AFF&color=fff&font-size=0.33`;
      e.target.onerror = () => {
        e.target.style.display = 'none';
      };
    };

    return (
      <div className="flex items-center gap-3 min-w-0">
        <div className="relative">
          {avatarUrl && (
            <img
              src={avatarUrl}
              width={40}
              height={40}
              className="rounded-full object-cover border-2 border-blue-100 shadow-sm"
              onError={handleImageError}
              alt={expert.Name}
            />
          )}
          {/* Online indicator for active experts */}
          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 text-sm truncate">{expert.Name}</div>
          <div className="text-xs text-gray-500 truncate">{expert.Role}</div>
        </div>
      </div>
    );
  };

  

  

  return (
    <div className="bg-white lora">
      {/* Widget Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 pt-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">The Players' Picks</h1>

          {/* Tab Navigation */}
          <div className="w-full mb-4">
            <div className="flex w-full border-b border-gray-200">
              {races.map((race, idx) => {
                const isActive = activeRaceTab === idx;
                return (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.preventDefault();
                      handleTabChange(idx);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleTabChange(idx);
                    }}
                    type="button"
                    className={`flex-1 px-2 py-3 text-xs sm:text-sm font-semibold capitalize transition-colors relative font-sans focus:outline-none whitespace-nowrap overflow-hidden cursor-pointer ${
                      isActive
                        ? 'text-gray-900 bg-white'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ 
                      WebkitTapHighlightColor: 'transparent',
                      WebkitTouchCallout: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                      touchAction: 'manipulation'
                    }}
                    aria-selected={isActive}
                    role="tab"
                    tabIndex={isActive ? 0 : -1}
                  >
                    <span className="truncate block">{race.name.toLowerCase()}</span>
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Race Information */}
          {currentRace && (
            <div className="mb-4">
              <div className="flex justify-between items-center">
                <div>
                  <a
                    href={currentRace.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                  >
                    {currentRace.name}
                  </a>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 capitalize tracking-wide">Purse</div>
                  <div className="text-xl font-bold text-gray-900">${currentRace.purse}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expert List for Selected Race */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white">
          {currentRace && (
            <>

              {/* Expert List */}
              <div className="divide-y divide-gray-200">
                {predictions.map((prediction, idx) => {
                  const expert = prediction.fields;
                  const picks = currentRace.picks;
                  
                  return (
                    <div key={idx} className="px-1 py-3 hover:bg-gray-50/50 transition-colors">
                      {/* Expert Profile */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-200 bg-blue-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                              {(() => {
                                // Check multiple possible avatar sources
                                let avatarUrl = null;
                                if (expert.Avatar && expert.Avatar[0] && expert.Avatar[0].url) {
                                  avatarUrl = expert.Avatar[0].url;
                                } else if (expert.AvatarURL) {
                                  avatarUrl = expert.AvatarURL;
                                } else if (expert.Image) {
                                  avatarUrl = expert.Image;
                                }
                                
                                return avatarUrl ? (
                                  <img
                                    src={avatarUrl}
                                    className="object-cover rounded-full h-12 w-12"
                                    alt={expert.Name}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                    onLoad={() => {}}
                                  />
                                ) : null;
                              })()}
                              <span className={(() => {
                                let hasAvatar = false;
                                if (expert.Avatar && expert.Avatar[0] && expert.Avatar[0].url) hasAvatar = true;
                                else if (expert.AvatarURL) hasAvatar = true;
                                else if (expert.Image) hasAvatar = true;
                                return hasAvatar ? 'hidden' : '';
                              })()}>
                                {expert.Name ? expert.Name.charAt(0) : '?'}
                              </span>
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900 text-sm">{expert.Name}</h3>
                              <p className="text-xs text-gray-600 font-normal">{expert.Role}</p>
                            </div>
                          </div>
                          
                          {/* Follow Button */}
                          {expert.URL && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.open(expert.URL, '_blank', 'noopener,noreferrer');
                              }}
                              type="button"
                              className="px-3 py-1.5 bg-transparent hover:bg-[#2c2e32] text-[#2c2e32] hover:text-white text-xs font-bold uppercase border border-[#2c2e32] cursor-pointer transition-all duration-200 font-sans touch-manipulation"
                              style={{ borderColor: '#2c2e32', WebkitTapHighlightColor: 'transparent' }}
                            >
                              Follow
                            </button>
                          )}
                        </div>
                        
                        {/* Performance Stats Grid */}
                        <div className="grid grid-cols-3 gap-1.5 sm:gap-3 text-center">
                          <div>
                            {/* Mobile: extra compact, Desktop: stacked */}
                            <div className="sm:hidden">
                              <div className="text-xs text-gray-500 font-semibold leading-none">Picks</div>
                              <div className="font-bold text-gray-900 text-xs mt-0.5">{expert.TopSelections || '0-0'}</div>
                            </div>
                            <div className="hidden sm:block">
                              <div className="text-xs text-gray-500 mb-0.5 font-semibold">Top Selections</div>
                              <div className="font-bold text-gray-900 text-sm">{expert.TopSelections || '0-0'}</div>
                            </div>
                          </div>
                          <div>
                            {/* Mobile: extra compact, Desktop: stacked */}
                            <div className="sm:hidden">
                              <div className="text-xs text-gray-500 font-semibold leading-none">Exacta</div>
                              <div className="font-semibold text-green-600 text-xs mt-0.5">{expert.ExactaEarnings || '0'}</div>
                            </div>
                            <div className="hidden sm:block">
                              <div className="text-xs text-gray-500 mb-0.5 font-semibold">Exacta Earnings</div>
                              <div className="font-semibold text-green-600 text-sm">{expert.ExactaEarnings || '0'}</div>
                            </div>
                          </div>
                          <div>
                            {/* Mobile: extra compact, Desktop: stacked */}
                            <div className="sm:hidden">
                              <div className="text-xs text-gray-500 font-semibold leading-none">Trifecta</div>
                              <div className="font-semibold text-green-600 text-xs mt-0.5">{expert.TrifectaEarnings || '0'}</div>
                            </div>
                            <div className="hidden sm:block">
                              <div className="text-xs text-gray-500 mb-0.5 font-semibold">Trifecta Earnings</div>
                              <div className="font-semibold text-green-600 text-sm">{expert.TrifectaEarnings || '0'}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Picks Section - Single Row */}
                      <div className="grid grid-cols-4 bg-gray-50 rounded-lg overflow-hidden">
                        {/* One Pick */}
                        <div className="p-1.5 sm:p-2.5 text-center border-r border-gray-300 bg-white">
                          <div className="text-xs font-semibold text-gray-500 capitalize mb-0.5">1st</div>
                          <div className="font-bold text-gray-900 text-xs sm:text-sm leading-tight">{expert[picks[0]] || '—'}</div>
                        </div>

                        {/* Two Pick */}
                        <div className="p-1.5 sm:p-2.5 text-center border-r border-gray-300 bg-white">
                          <div className="text-xs font-semibold text-gray-500 capitalize mb-0.5">2nd</div>
                          <div className="font-bold text-gray-900 text-xs sm:text-sm leading-tight">{expert[picks[1]] || '—'}</div>
                        </div>

                        {/* Three Pick */}
                        <div className="p-1.5 sm:p-2.5 text-center border-r border-gray-300 bg-white">
                          <div className="text-xs font-semibold text-gray-500 capitalize mb-0.5">3rd</div>
                          <div className="font-bold text-gray-900 text-xs sm:text-sm leading-tight">{expert[picks[2]] || '—'}</div>
                        </div>

                        {/* Alternate Pick */}
                        <div className="p-1.5 sm:p-2.5 text-center bg-white">
                          <div className="text-xs font-semibold text-gray-500 capitalize mb-0.5">Alt</div>
                          <div className="font-bold text-gray-900 text-xs sm:text-sm leading-tight">{expert[picks[3]] || '—'}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Widget Footer with Logos */}
              <div className="px-2 py-4 border-t border-gray-100">
                <div className="flex flex-row justify-between items-center gap-3">
                  <img 
                    src="https://www.americasbestracing.net/sites/all/themes/custom/abr_theme/img/logos/equibase.svg"
                    width={80}
                    alt="Equibase"
                    className="opacity-60 hover:opacity-80 transition-opacity"
                  />
                  <a 
                    href="https://tally.site/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    <span className="text-sm font-medium">Powered by</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="120" height="30" fill="none" className="h-6 w-auto">
                      <g clipPath="url(#tallysight-logo-clip)">
                        <circle cx="12" cy="12" r="12" fill="#008AFF"></circle>
                        <circle cx="12" cy="11.999" r="10.757" fill="#000" stroke="#008AFF" strokeWidth=".4"></circle>
                        <path fill="#fff" d="M14.704 16.27q-1.428 0-2.254-.65-.814-.664-.814-1.791 0-.114.025-.364l.025-.175a.24.24 0 0 1 .063-.138.22.22 0 0 1 .137-.05h1.891q.088 0 .126.05.05.051.037.138l-.012.075q-.039.351.275.626.325.264.927.263.488 0 .7-.225.214-.226.214-.463 0-.251-.238-.376-.225-.126-.801-.3l-.339-.113q-1.065-.338-1.79-.89-.714-.563-.714-1.602 0-.876.463-1.553.477-.675 1.277-1.052a4.2 4.2 0 0 1 1.779-.375q.876 0 1.565.313.7.313 1.09.901.387.576.387 1.328 0 .112-.025.363l-.012.112a.2.2 0 0 1-.075.138.19.19 0 0 1-.138.05h-1.878a.19.19 0 0 1-.138-.05q-.037-.05-.025-.138v-.012a.7.7 0 0 0-.088-.463.9.9 0 0 0-.375-.376q-.25-.15-.626-.15-.414 0-.664.188a.56.56 0 0 0-.25.488q0 .288.325.488.338.188 1.265.501.1.038.764.263.675.213 1.127.739.45.514.45 1.252 0 .1-.025.326-.162 1.264-1.152 1.99-.99.714-2.48.714M12.057 7.305q.075 0 .113.05.05.05.038.138l-.2 1.652a.24.24 0 0 1-.063.138.19.19 0 0 1-.138.05H9.778q-.062 0-.087.075l-.79 6.474a.2.2 0 0 1-.074.138.19.19 0 0 1-.138.05H6.773a.19.19 0 0 1-.138-.05q-.037-.051-.025-.138l.802-6.474q0-.075-.063-.075H5.383a.19.19 0 0 1-.137-.05q-.038-.05-.025-.138l.2-1.652a.24.24 0 0 1 .062-.138.23.23 0 0 1 .15-.05z"></path>
                        <path className="fill-current" d="M30.52 17V9.205h2.076V17zm-2.272-7.503V7.644h6.614v1.853zm8.134 7.605q-.69 0-1.218-.28a2.16 2.16 0 0 1-.82-.749 1.95 1.95 0 0 1-.291-1.053q0-.617.33-1.092.336-.476.965-.75.634-.272 1.523-.272h1.632v1.257h-1.39q-.547 0-.838.177-.285.178-.286.553 0 .222.114.38a.8.8 0 0 0 .317.242q.21.082.47.082.242 0 .565-.095.324-.096.559-.317.24-.223.241-.597v-.794l-.095-.298v-.933a.8.8 0 0 0-.076-.356.7.7 0 0 0-.216-.279 1 1 0 0 0-.356-.171 1.6 1.6 0 0 0-.476-.064q-.304 0-.654.07a4 4 0 0 0-.672.184 2.6 2.6 0 0 0-.559.267l-.667-1.51q.255-.147.667-.293.42-.146.952-.241.54-.102 1.155-.102.96 0 1.524.248.565.24.838.622.273.374.355.806.09.425.09.794v2.056q0 .349.082.533.089.178.31.248l.153.05-.705 1.677-.235-.077q-.59-.196-.888-.501-.298-.304-.356-.61l.445.305h-.787l.526-.159q-.203.407-.818.724-.61.318-1.41.318m6.561 0q-.597 0-1.003-.26a1.7 1.7 0 0 1-.603-.699 2.2 2.2 0 0 1-.203-.952V7.225h2.006v7.661q0 .165.044.286.045.114.14.184a.5.5 0 0 0 .267.063.6.6 0 0 0 .165-.019l.114-.031v1.574a2.4 2.4 0 0 1-.432.114q-.24.045-.495.045m3.254 0q-.597 0-1.003-.26a1.7 1.7 0 0 1-.603-.699 2.2 2.2 0 0 1-.203-.952V7.225h2.006v7.661q0 .165.044.286.044.114.14.184a.5.5 0 0 0 .266.063.6.6 0 0 0 .165-.019l.114-.031v1.574a2.4 2.4 0 0 1-.431.114 2.7 2.7 0 0 1-.495.045m2.618 2.666a4 4 0 0 1-.78-.077 5 5 0 0 1-.66-.158l.691-1.606q.147.069.324.114t.337.044q.316 0 .507-.133a.83.83 0 0 0 .286-.31q.096-.186.14-.337l.304-1.035-.146 1.047-2.913-7.147h2.107l1.289 3.796.317.996h.051l.286-.996 1.193-3.796h2.095l-2.882 7.592q-.114.291-.273.64a2.6 2.6 0 0 1-.432.655 2 2 0 0 1-.717.508q-.438.203-1.123.203m7.818-2.666a3.3 3.3 0 0 1-1.168-.21 3.1 3.1 0 0 1-.978-.59 2.5 2.5 0 0 1-.64-.902l1.574-.691q.158.298.476.564.316.267.749.267a1 1 0 0 0 .4-.076.7.7 0 0 0 .266-.21.416.416 0 0 0-.044-.59 1.3 1.3 0 0 0-.375-.229 4 4 0 0 0-.482-.165l-.457-.126a4.2 4.2 0 0 1-.914-.4 2.2 2.2 0 0 1-.692-.66q-.267-.4-.267-.972 0-.603.311-1.06.318-.463.857-.723.546-.26 1.244-.26.87 0 1.562.4.698.4.99 1.06l-1.542.666a1.5 1.5 0 0 0-.356-.394.88.88 0 0 0-.584-.19.8.8 0 0 0-.495.152.46.46 0 0 0-.197.381q0 .228.197.368.196.14.502.241.304.102.634.197.255.07.59.203.338.127.654.35.324.222.534.57.216.35.215.858 0 .64-.355 1.136-.35.489-.933.762-.577.273-1.276.273M59.741 17v-6.83h2.005V17zm1.009-7.61q-.515-.001-.87-.356a1.18 1.18 0 0 1-.355-.864q0-.52.355-.87.356-.354.87-.355.514 0 .863.356.356.349.356.87 0 .507-.356.863-.349.355-.863.355m4.834 10.378q-.85 0-1.562-.21-.705-.203-1.11-.419l.66-1.574q.355.215.825.368.476.159.965.159.42 0 .87-.14t.767-.444q.318-.299.318-.787v-.66h-.21l.299-.648v-3.84l-.489-.743h.463v-.666h1.822v6.442q0 .782-.266 1.371-.267.591-.756.99a3.2 3.2 0 0 1-1.142.598q-.66.203-1.454.203m-.248-2.895q-.54 0-1.072-.203-.534-.21-.971-.629a3.1 3.1 0 0 1-.692-1.06q-.26-.634-.26-1.491 0-1.143.444-1.898.45-.762 1.155-1.143.705-.387 1.466-.387.882 0 1.543.45.666.451 1.034 1.207.368.75.368 1.663 0 .945-.393 1.74-.393.792-1.073 1.275a2.63 2.63 0 0 1-1.549.476m.496-1.631q.456 0 .806-.229.348-.228.552-.635a2.1 2.1 0 0 0 .203-.952q0-.546-.197-.933a1.44 1.44 0 0 0-.54-.603 1.5 1.5 0 0 0-.793-.21q-.45 0-.806.216-.355.21-.558.603a2.05 2.05 0 0 0-.197.927q0 .534.197.946.195.406.54.641.342.229.793.229M70.088 17V7.225h2.006v3.573h.368l-.68.705q.116-.482.458-.8a2.3 2.3 0 0 1 .819-.476 3.1 3.1 0 0 1 .99-.159q.755 0 1.27.318.513.31.774.863.267.552.266 1.276V17h-2.005v-4.037q0-.622-.299-.927-.291-.305-.787-.305-.33.001-.597.146a1.05 1.05 0 0 0-.418.42q-.16.272-.16.685V17zm10.166.102q-1.263 0-1.879-.699-.615-.705-.615-1.802V8.202h2.005v6.348q0 .438.21.635.216.19.527.19.145 0 .285-.038t.248-.095l.647 1.58q-.285.14-.666.21-.375.07-.762.07m-3.637-5.269V10.17h4.729v1.663z"></path>
                      </g>
                      <defs>
                        <clipPath id="tallysight-logo-clip">
                          <path d="M0 0h120v30H0z"></path>
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default HorseRaceWidget;